import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon,
  TrophyIcon,
  BoltIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const ComparisonTable = ({ onSelectPlan }) => {
  const { t } = useTranslation();

  const features = [
    {
      category: t('subscription.commissionFees'),
      icon: CurrencyDollarIcon,
      items: [
        {
          name: t('subscription.platformCommissionFull'),
          free: '18%',
          premium: '0%',
          isPremiumHighlight: true
        },
        {
          name: t('subscription.gatewayFees'),
          free: '2.5% + 1',
          premium: '2.5% + 1',
          note: t('subscription.gatewayFeesNote')
        }
      ]
    },
    {
      category: t('subscription.exchangeServices'),
      icon: BoltIcon,
      items: [
        {
          name: t('subscription.freeExchangeOperations'),
          free: t('subscription.notAvailable'),
          premium: `10 ${t('subscription.yearlyOperations')}`,
          isPremiumHighlight: true
        }
      ]
    },
    {
      category: t('subscription.supportReports'),
      icon: ShieldCheckIcon,
      items: [
        {
          name: t('subscription.technicalSupport'),
          free: t('subscription.basicSupport'),
          premium: t('subscription.premiumSupportWithPriority'),
          isPremiumHighlight: true
        }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const renderFeatureValue = (value, isPremium = false, isHighlight = false) => {
    if (typeof value === 'boolean') {
      return (
        <div className="flex justify-center">
          {value ? (
            <CheckCircleIcon className={`w-6 h-6 ${isHighlight ? 'text-brand-green' : 'text-green-500'}`} />
          ) : (
            <XCircleIcon className="w-6 h-6 text-gray-400" />
          )}
        </div>
      );
    }

    return (
      <span className={`text-center block ${
        isHighlight 
          ? 'text-brand-blue font-bold' 
          : isPremium 
            ? 'text-gray-700 font-medium' 
            : 'text-gray-600'
      }`}>
        {value}
      </span>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">

      {/* Comparison Table */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 overflow-hidden"
      >
        {/* Table Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="grid grid-cols-12 gap-4 p-6">
            <div className="col-span-6 md:col-span-4">
              <h3 className="text-lg font-bold text-gray-800">{t('subscription.features')}</h3>
            </div>
            <div className="col-span-3 md:col-span-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-gray-600" />
                <span className="font-bold text-gray-800">{t('subscription.freePlan')}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{t('subscription.freeForever')}</p>
            </div>
            <div className="col-span-3 md:col-span-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <TrophyIcon className="w-5 h-5 text-brand-blue" />
                <span className="font-bold bg-gradient-to-r from-brand-blue to-brand-green bg-clip-text text-transparent">
                  {t('subscription.badeelPlus')}
                </span>
              </div>
              <p className="text-sm text-brand-blue mt-1">{t('subscription.yearlyPrice')}</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="divide-y divide-gray-100">
          {features.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              variants={itemVariants}
              className="p-6"
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-brand-blue to-brand-green rounded-lg flex items-center justify-center">
                  <category.icon className="w-5 h-5 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-800">{category.category}</h4>
              </div>

              {/* Category Features */}
              <div className="space-y-4">
                {category.items.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    variants={itemVariants}
                    className="grid grid-cols-12 gap-4 items-center py-3 hover:bg-gray-50/50 rounded-lg transition-colors"
                  >
                    <div className="col-span-6 md:col-span-4">
                      <span className="text-gray-700 font-medium">{feature.name}</span>
                      {feature.note && (
                        <p className="text-xs text-gray-500 mt-1">{feature.note}</p>
                      )}
                    </div>
                    <div className="col-span-3 md:col-span-4 px-4">
                      {renderFeatureValue(feature.free, false, false)}
                    </div>
                    <div className="col-span-3 md:col-span-4 px-4">
                      <div className={`${feature.isPremiumHighlight ? 'bg-gradient-to-r from-brand-blue/10 to-brand-green/10 rounded-lg p-2' : ''}`}>
                        {renderFeatureValue(feature.premium, true, feature.isPremiumHighlight)}
                        {feature.isPremiumHighlight && (
                          <SparklesIcon className="w-4 h-4 text-brand-green mx-auto mt-1" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-brand-blue/5 to-brand-green/5 border-t border-gray-200 p-6"
        >
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-6 md:col-span-4">
              <div className="flex items-center gap-2">
                <HeartIcon className="w-5 h-5 text-brand-blue" />
                <span className="font-bold text-gray-800">{t('subscription.startNow')}</span>
              </div>
            </div>
            <div className="col-span-3 md:col-span-4 text-center">
              <button
                disabled
                className="px-6 py-3 bg-gray-100 text-gray-500 rounded-xl font-medium cursor-not-allowed"
              >
                {t('subscription.currentPlan')}
              </button>
            </div>
            <div className="col-span-3 md:col-span-4 text-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onSelectPlan?.({ type: 'badeel_plus' })}
                className="px-6 py-3 bg-brand-blue text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <span className="flex items-center justify-center gap-2">
                  <SparklesIcon className="w-4 h-4" />
                  {t('subscription.subscribeNow')}
                </span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Savings Calculator */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-12 bg-gradient-to-r from-brand-blue/10 to-brand-green/10 rounded-3xl p-8 text-center"
      >
        <ChartBarIcon className="w-12 h-12 text-brand-blue mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-800 mb-4">
          {t('subscription.calculateSavings')}
        </h3>
        <p className="text-gray-600 mb-6">
          {t('subscription.savingsDescription')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <div className="bg-white/50 rounded-2xl p-6">
            <h4 className="font-bold text-gray-800 mb-2">{t('subscription.withFreePlan')}</h4>
            <p className="text-sm text-gray-600">{t('subscription.freeCommissionExample')}</p>
            <p className="text-2xl font-bold text-red-600 mt-2">{t('subscription.freePlanCost')}</p>
          </div>
          <div className="bg-white/50 rounded-2xl p-6">
            <h4 className="font-bold text-brand-blue mb-2">{t('subscription.withBadeelPlus')}</h4>
            <p className="text-sm text-gray-600">{t('subscription.yearlySubscriptionOnly')}</p>
            <p className="text-2xl font-bold text-brand-green mt-2">{t('subscription.badeelPlusCost')}</p>
          </div>
        </div>
        <p className="text-lg font-bold text-brand-green mt-6">
          {t('subscription.yearlySavings')}
        </p>
      </motion.div>
    </div>
  );
};

export default ComparisonTable;
